/**
 * ZIP без сжатия (метод 0) — для архива «Серия».
 *
 * Своими руками, а не библиотекой: в архиве лежат PNG, они уже сжаты, и deflate
 * дал бы проценты за целую зависимость в поставке. Формат здесь минимальный —
 * локальные заголовки, центральный каталог, EOCD, — и потому проверяется
 * побайтно тестом: битый архив человек увидит дома, когда наши логи ему уже
 * ничем не помогут.
 *
 * Дескрипторов данных не пишем (размеры известны заранее): их не любят
 * встроенные распаковщики Windows. ZIP64 не нужен — пять кадров это единицы
 * мегабайт.
 */

export interface ZipEntry {
  /** Путь внутри архива. Только ASCII — см. `assertAscii`. */
  name: string
  data: Uint8Array
}

const LOCAL_SIG = 0x04034b50
const CENTRAL_SIG = 0x02014b50
const EOCD_SIG = 0x06054b50
/** Версия распаковщика: 2.0 — минимум, понимающий каталог. */
const VERSION = 20
const STORED = 0

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[i] = c >>> 0
  }
  return table
})()

/** CRC-32/ISO-HDLC — та контрольная сумма, которую ждёт формат ZIP. */
export function crc32(data: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

/**
 * Имена внутри архива — только ASCII.
 *
 * Кириллица без флага UTF-8 приезжает в Windows крокозябрами, а с флагом её
 * теряют старые распаковщики. Имена нам подаёт транслит (`map/translit.ts`), и
 * если он однажды перестанет работать, узнать об этом надо здесь — падением, а
 * не по звонку получателя файла.
 */
function assertAscii(name: string): void {
  // eslint-disable-next-line no-control-regex
  if (!/^[\x20-\x7e]+$/.test(name)) {
    throw new Error(`zipStore: entry name must be ASCII, got ${JSON.stringify(name)}`)
  }
}

/** Дата и время в формате MS-DOS: секунды с шагом 2, год от 1980. */
function dosStamp(at: Date): { time: number; date: number } {
  const year = Math.max(1980, at.getUTCFullYear())
  return {
    time: (at.getUTCHours() << 11) | (at.getUTCMinutes() << 5) | (at.getUTCSeconds() >> 1),
    date: ((year - 1980) << 9) | ((at.getUTCMonth() + 1) << 5) | at.getUTCDate(),
  }
}

/** Курсор записи: little-endian, как того требует формат. */
class Writer {
  private view: DataView
  private bytes: Uint8Array<ArrayBuffer>
  private at = 0

  constructor(size: number) {
    this.bytes = new Uint8Array(size)
    this.view = new DataView(this.bytes.buffer)
  }

  u16(v: number): void {
    this.view.setUint16(this.at, v, true)
    this.at += 2
  }

  u32(v: number): void {
    this.view.setUint32(this.at, v >>> 0, true)
    this.at += 4
  }

  raw(v: Uint8Array): void {
    this.bytes.set(v, this.at)
    this.at += v.length
  }

  get offset(): number {
    return this.at
  }

  get done(): Uint8Array<ArrayBuffer> {
    return this.bytes
  }
}

/**
 * Собрать архив. `at` передаётся явно: одинаковый вход обязан давать одинаковые
 * байты, иначе тест формата пришлось бы писать «примерно».
 */
export function zipStore(entries: ZipEntry[], at: Date = new Date()): Uint8Array<ArrayBuffer> {
  const stamp = dosStamp(at)
  const encoder = new TextEncoder()
  const prepared = entries.map(e => {
    assertAscii(e.name)
    return { name: encoder.encode(e.name), data: e.data, crc: crc32(e.data) }
  })

  const localSize = prepared.reduce((sum, e) => sum + 30 + e.name.length + e.data.length, 0)
  const centralSize = prepared.reduce((sum, e) => sum + 46 + e.name.length, 0)
  const w = new Writer(localSize + centralSize + 22)

  const offsets: number[] = []
  for (const e of prepared) {
    offsets.push(w.offset)
    w.u32(LOCAL_SIG)
    w.u16(VERSION)
    w.u16(0) // флаги: имена ASCII, шифрования нет
    w.u16(STORED)
    w.u16(stamp.time)
    w.u16(stamp.date)
    w.u32(e.crc)
    w.u32(e.data.length) // сжатый размер = исходному: храним как есть
    w.u32(e.data.length)
    w.u16(e.name.length)
    w.u16(0) // extra
    w.raw(e.name)
    w.raw(e.data)
  }

  const centralAt = w.offset
  prepared.forEach((e, i) => {
    w.u32(CENTRAL_SIG)
    w.u16(VERSION) // version made by
    w.u16(VERSION) // version needed
    w.u16(0)
    w.u16(STORED)
    w.u16(stamp.time)
    w.u16(stamp.date)
    w.u32(e.crc)
    w.u32(e.data.length)
    w.u32(e.data.length)
    w.u16(e.name.length)
    w.u16(0) // extra
    w.u16(0) // comment
    w.u16(0) // номер диска
    w.u16(0) // внутренние атрибуты
    w.u32(0) // внешние атрибуты
    w.u32(offsets[i])
    w.raw(e.name)
  })

  // Длину каталога берём ДО записи EOCD: `w.offset` уже уехал бы на его байты.
  const centralBytes = w.offset - centralAt
  w.u32(EOCD_SIG)
  w.u16(0) // номер диска
  w.u16(0) // диск с каталогом
  w.u16(prepared.length)
  w.u16(prepared.length)
  w.u32(centralBytes)
  w.u32(centralAt)
  w.u16(0) // комментарий архива
  return w.done
}
